import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import QrScanner from 'qr-scanner';
import { FlashScannerButton } from './FlashScannerButton';

import './QRScanner.scss';
import { useTranslation } from 'react-i18next';

let stopScan = false;
let scanner = null;
function QRScanner({ handleResult }) {
  const { t } = useTranslation(['description']);
  const [hasFlash, setHasFlash] = useState(false);
  const [cameraList, setCameraList] = useState([
    {
      value: 'environment',
      label: 'Cámara principal',
    },
    { value: 'user', label: 'Cámara frontal' },
  ]);
  const [camera, setCamera] = useState('environment');
  const [loading, setLoading] = useState(true);
  const [, setError] = useState('');

  const updateFlashAvailability = () => {
    if (!scanner) return;
    scanner.hasFlash().then((hasFlash) => {
      setHasFlash(hasFlash);
    });
  };

  const handleSelected = (e) => {
    setCamera(e.target.value);
    if (scanner) {
      scanner.setCamera(e.target.value).then(updateFlashAvailability);
    }
  };

  const handleFlash = async () => {
    if (!scanner) return;
    await scanner.toggleFlash();
  };

  const userScan = useCallback(async (isScan = true) => {
    await new Promise((r) => setTimeout(r, 1000));
    if (isScan) stopScan = true;
    if (!isScan) return;
    stopScan = false;
    scanner = new QrScanner(
      document.getElementById('scan-view'),
      (result) => {
        handleResult(result.data, scanner);
        setError('');
      },
      {
        onDecodeError: (err) => {
          setError(err);
        },
        maxScansPerSecond: 1,
        highlightScanRegion: true,
        returnDetailedScanResult: true,
        highlightCodeOutline: true,
      }
    );

    scanner
      .start()
      .then(() => {
        setLoading(false);
        updateFlashAvailability();

        QrScanner.listCameras(true).then((cameras) => {
          cameras.forEach((camera) =>
            setCameraList([
              ...cameraList,
              { value: camera.id, label: camera.label },
            ])
          );
        });
      })
      .catch((error) => {
        setLoading(false);
      });

    while (!stopScan) {
      await new Promise((r) => setTimeout(r, 100));
    }
    if (scanner && typeof scanner.stop === 'function') {
      try {
        await scanner.stop();
      } catch {}
    }
    if (scanner && typeof scanner.destroy === 'function') {
      try {
        scanner.destroy();
      } catch {}
    }
    scanner = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    userScan();

    return () => {
      stopScan = true;
      if (scanner && typeof scanner.stop === 'function') {
        try {
          scanner.stop();
        } catch {}
      }
      if (scanner && typeof scanner.destroy === 'function') {
        try {
          scanner.destroy();
        } catch {}
      }
      scanner = null;
      setError('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      mb={4}
      sx={{
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          borderStyle: 'dashed',
          padding: '5px',
          borderRadius: '16px',
          backgroundColor: 'white',
        }}
      >
        {loading && (
          <CircularProgress
            sx={{ position: 'absolute', top: '45%', left: '45%' }}
            variant="indeterminate"
          />
        )}

        <video id="scan-view"></video>
      </Box>

      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        mt={1}
      >
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="camera-label">
              {t('qr_choose_camera_label', { ns: 'description' })}
            </InputLabel>
            <Select
              autoWidth
              labelId="camera-label"
              id="camera-id"
              value={camera}
              label={t('qr_choose_camera_label', { ns: 'description' })}
              onChange={handleSelected}
              defaultValue="environment"
            >
              {cameraList.map((camera, index) => (
                <MenuItem key={`camera-${index}`} value={camera.value}>
                  {camera.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item>
          {hasFlash && (
            <FlashScannerButton
              isFlashOn={scanner.isFlashOn()}
              handleFlash={handleFlash}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default QRScanner;
